/**
 * useWalletsMount — monte Apple/Google Pay du bon provider selon
 * `preferred_provider` de l'org. Wrapper partagé entre toutes les pages
 * de paiement Donoor (/c, /donate, /a, /b, /pay, /pay/upgrade).
 *
 * Règles (Karim 2026-06-21) :
 *  - **usesPaypalWallets = false** (preferred=stripe ou Stripe-only) →
 *      mount `stripeCheckout.mountExpressCheckout("express-checkout-element")`
 *      qui rend Apple+Google Pay groupés (1 element Stripe).
 *  - **usesPaypalWallets = true** (preferred=paypal ou PayPal-only) →
 *      mount `paypalCheckout.mountApplePay()` + `paypalCheckout.mountGooglePay()`
 *      sur 2 containers séparés. Chacun throw NOT_ELIGIBLE si l'org/device
 *      ne supporte pas → on cache silencieusement (try/catch + log info).
 *
 * Le composant `DonateExpressCheckoutSection` fournit déjà les 3 containers
 * (`#express-checkout-element`, `#paypal-applepay-container`,
 * `#paypal-googlepay-container`) — ce composable s'occupe juste du mount.
 *
 * Idempotent : safe à appeler plusieurs fois (les providers gèrent leur
 * propre dédup interne).
 */
import type { useCheckout } from "@donoor/checkout";
import type { ComputedRef, Ref } from "vue";

interface UseWalletsMountOptions {
  /** Instance Stripe (useStripeDonationCheckout().checkout). */
  stripeCheckout: ReturnType<typeof useCheckout>;
  /** Instance PayPal (usePaypalDonationCheckout().checkout). */
  paypalCheckout: ReturnType<typeof useCheckout>;
  /** Mode PayPal-only (pas de Stripe configuré sur l'org). */
  isPaypalOnlyMode: Ref<boolean> | ComputedRef<boolean>;
  /** Provider primaire imposé par l'org (preferred_provider). */
  usesPaypalWallets: Ref<boolean> | ComputedRef<boolean>;
  /** IDs DOM des containers (defaults compatibles avec DonateExpressCheckoutSection). */
  containerIds?: {
    stripeExpress?: string;
    paypalApplePay?: string;
    paypalGooglePay?: string;
  };
}

export function useWalletsMount(opts: UseWalletsMountOptions) {
  const stripeExpressId = opts.containerIds?.stripeExpress || "express-checkout-element";
  const paypalApplePayId = opts.containerIds?.paypalApplePay || "paypal-applepay-container";
  const paypalGooglePayId = opts.containerIds?.paypalGooglePay || "paypal-googlepay-container";

  /**
   * Monte le wallet provider primaire (Stripe Express OU PayPal Apple+Google).
   * Idempotent. Logs les NOT_ELIGIBLE en info (pas en error) — c'est un état
   * légitime quand le device ou la capability ne sont pas dispos.
   */
  async function mountPrimaryWallets(): Promise<void> {
    // PayPal-only ET preferred=paypal → wallets PayPal.
    if (opts.isPaypalOnlyMode.value || opts.usesPaypalWallets.value) {
      // Apple Pay PayPal — best-effort, cache silencieusement si non éligible.
      try {
        await opts.paypalCheckout.mountApplePay(paypalApplePayId);
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (code === "PAYPAL_APPLEPAY_NOT_ELIGIBLE") {
          console.info("[useWalletsMount] Apple Pay PayPal non éligible — bouton caché");
          hideContainer(paypalApplePayId);
        } else {
          console.error("[useWalletsMount] mountApplePay error:", err);
          hideContainer(paypalApplePayId);
        }
      }
      // Google Pay PayPal — best-effort.
      try {
        await opts.paypalCheckout.mountGooglePay(paypalGooglePayId);
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (code === "PAYPAL_GOOGLEPAY_NOT_ELIGIBLE") {
          console.info("[useWalletsMount] Google Pay PayPal non éligible — bouton caché");
          hideContainer(paypalGooglePayId);
        } else {
          console.error("[useWalletsMount] mountGooglePay error:", err);
          hideContainer(paypalGooglePayId);
        }
      }
      return;
    }

    // preferred=stripe (ou Stripe-only) → Express Checkout Stripe.
    try {
      await opts.stripeCheckout.mountExpressCheckout(stripeExpressId);
    } catch (err) {
      console.error("[useWalletsMount] mountExpressCheckout Stripe error:", err);
    }
  }

  /**
   * Cache un container DOM. Utilisé quand le wallet correspondant n'est
   * pas éligible — évite un container vide visible à la place du bouton.
   */
  function hideContainer(id: string): void {
    if (typeof document === "undefined") return;
    const el = document.getElementById(id);
    if (el) el.style.display = "none";
  }

  return { mountPrimaryWallets };
}
